import React from "react";
import BookOpenOutlineIcon from "@src/lib/icons/18px/book-open-outline";
import ContentAvatar, {
  CONTENT_TYPE_COPY,
  LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE,
} from "@src/features/contentLibrary/ContentAvatar";
import { cn } from "@src/lib/utils/cn";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import type { OpenGraphData } from "@src/features/chat/types";
import type { ChatReferenceLibraryItem } from "@src/features/aiAssist/hooks/useChatReferences";
import {
  OPUS_AGENT_CARD_CLASS,
  OPUS_AGENT_CARD_INTERACTIVE_CLASS,
  OpusAgentCardAction,
  OpusAgentCardMedia,
  OpusAgentCardText,
} from "./OpusAgentCard";

// One source under an answer: a library item or a Help Center article. The
// whole card opens it in a new tab.
const CardBody: React.FC<{
  media: React.ReactNode;
  mediaClassName?: string;
  title: string;
  label: string;
  interactive: boolean;
}> = ({ media, mediaClassName, title, label, interactive }) => (
  <>
    <OpusAgentCardMedia className={mediaClassName}>{media}</OpusAgentCardMedia>
    <OpusAgentCardText
      title={title}
      subtitle={label}
      titleTestId="opus-agent-source-title"
      subtitleTestId="opus-agent-source-label"
    />
    {interactive && <OpusAgentCardAction />}
  </>
);

export const OpusAgentLibraryItemSource: React.FC<{
  libraryItem?: ChatReferenceLibraryItem;
  // The citation's own title, for an item that did not load.
  fallbackTitle: string;
}> = ({ libraryItem, fallbackTitle }) => {
  if (!libraryItem)
    return (
      <div className={OPUS_AGENT_CARD_CLASS} data-testid="opus-agent-source">
        <CardBody
          media={null}
          mediaClassName="bg-bg-tertiary"
          title={fallbackTitle || "Unknown item"}
          label="Unavailable"
          interactive={false}
        />
      </div>
    );
  const contentType = LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[libraryItem.type];
  const title =
    libraryItem.name.myTranslation ||
    `Untitled ${CONTENT_TYPE_COPY[contentType]}`;
  return (
    <a
      href={`/library/library-item/${libraryItem.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(OPUS_AGENT_CARD_CLASS, OPUS_AGENT_CARD_INTERACTIVE_CLASS)}
      data-testid="opus-agent-source"
      aria-label={`${title}, ${CONTENT_TYPE_COPY[contentType]} (opens in a new tab)`}
    >
      <CardBody
        media={
          <ContentAvatar
            size="40px"
            defaultBackgroundHashKey={libraryItem.id}
            contentType={contentType}
            coverImage={libraryItem.coverImage ?? undefined}
            className="size-full"
          />
        }
        title={title}
        label={CONTENT_TYPE_COPY[contentType]}
        interactive
      />
    </a>
  );
};

// Only http(s) links open; anything else renders as an inert card.
const safeUrl = (url: string): URL | null => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? parsed
      : null;
  } catch {
    return null;
  }
};

export const OpusAgentArticleSource: React.FC<{
  url: string;
  title: string | null;
}> = ({ url, title }) => {
  const parsed = safeUrl(url);
  // The agent cites articles by URL alone; the page's own title names them.
  const { data: ogData } = useOpenGraphDataQuery({
    variables: { url },
    errorPolicy: "ignore",
    skip: !parsed || !!title,
  });
  const ogTitle = (ogData?.OpenGraphData as OpenGraphData | null | undefined)?.[
    "og:title"
  ];
  const shownTitle =
    title || ogTitle?.trim() || parsed?.hostname || "Help Center article";
  const media = (
    <BookOpenOutlineIcon aria-hidden className="size-4 text-brand-text" />
  );
  if (!parsed)
    return (
      <div className={OPUS_AGENT_CARD_CLASS} data-testid="opus-agent-source">
        <CardBody
          media={media}
          mediaClassName="bg-brand-bg"
          title={shownTitle}
          label="Unavailable"
          interactive={false}
        />
      </div>
    );
  return (
    <a
      href={parsed.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(OPUS_AGENT_CARD_CLASS, OPUS_AGENT_CARD_INTERACTIVE_CLASS)}
      data-testid="opus-agent-source"
      aria-label={`${shownTitle}, Help Center article (opens in a new tab)`}
    >
      <CardBody
        media={media}
        mediaClassName="bg-brand-bg"
        title={shownTitle}
        label={`Help Center · ${parsed.hostname.replace(/^www\./, "")}`}
        interactive
      />
    </a>
  );
};
